import { BasicAuthServiceHelper } from '@/modules/Auth'
import APIRoutes from './APIRoutes'
import { IRI } from 'emt-library'
import { DateTime } from 'luxon'

export interface CartProduct {
  offer: string
  start: string
  end: string
}
export interface Tenders {
  animals: string[]
  message: string
  services: CartProduct[]
  deadline?: string // (default = lowest date in services)
  filters?: {
    latitude?: number // (default = current user latitude)
    longitude?: number // (default = current user longitude)
    distanceMin?: number // (default = 0)
    distanceMax?: number // (default = 20)
    syringeDrugAdministration?: boolean // (default = false)
    oralDrugAdministration?: boolean // (default = false)
  }
}

// --- Add these mappings and transformation logic (copied from useCart) ---
const serviceToOffer = {
  'garde-chez-eux': 'petsitter_home_sitting',
  'garde-chez-vous': 'customer_home_sitting',
  visite: {
    hour: 'visit_hour',
    half_hour: 'visit_half_hour',
  },
  promenade: {
    hour: 'walk_hour',
    half_hour: 'walk_half_hour',
  },
}

export const transformToCartProducts = (service: string, dateTimeInstances: any[]) => {
  const products: { offer: string; start: string; end: string }[] = []

  dateTimeInstances?.forEach((instance) => {
    if (!instance) return

    if (service === 'promenade' || service === 'visite') {
      instance.timePeriods?.forEach((period: any) => {
        const startDateTime = DateTime.fromISO(instance.date, { zone: 'Europe/Paris' }).set({
          hour: Number(period.timeStart.split(':')[0]),
          minute: Number(period.timeStart.split(':')[1]),
          second: 0,
          millisecond: 0,
        })

        const duration = serviceToOffer[service][period.serviceDuration]

        const minutesToAdd = duration === 'walk_half_hour' || duration === 'visit_half_hour' ? 30 : 60

        const endDateTime = startDateTime.plus({ minutes: minutesToAdd })

        products.push({
          offer: serviceToOffer[service][period.serviceDuration],
          start: startDateTime.toISO() ?? '',
          end: endDateTime.toISO() ?? '',
        })
      })
    } else {
      const start = DateTime.fromISO(instance.dateStart, { zone: 'Europe/Paris' }).set({
        hour: Number(instance.timeStart?.split(':')[0] ?? 0),
        minute: Number(instance.timeStart?.split(':')[1] ?? 0),
        second: 0,
        millisecond: 0,
      })

      const end = DateTime.fromISO(instance.dateEnd, { zone: 'Europe/Paris' }).set({
        hour: Number(instance.timeEnd?.split(':')[0] ?? 0),
        minute: Number(instance.timeEnd?.split(':')[1] ?? 0),
        second: 0,
        millisecond: 0,
      })

      products.push({
        offer: serviceToOffer[service],
        start: start.toISO() ?? '',
        end: end.toISO() ?? '',
      })
    }
  })

  return products
}

// Transform CallRequestLS to Tenders payload
export const buildTendersPayload = (callRequest: any) => {
  const serviceObj = callRequest.services?.[0]
  const service = serviceObj?.service
  const dateTimeInstances = serviceObj?.dateTimeInstances || []
  const extra = callRequest.additionalInformations?.summary || ''
  const finalDescription =
    extra.length > 0 ? `${callRequest.message}${callRequest.message ? '\n\n' : ''}${extra}` : callRequest.message
  return {
    animals: callRequest.animals,
    message: finalDescription,
    services: transformToCartProducts(service, dateTimeInstances),
    deadline: callRequest.deadline,
    filters: callRequest.filters,
  }
}

export const postTendersAsync = async (data: any) => {
  const { path, method } = APIRoutes.PostTenders

  const payload = buildTendersPayload(data)
  const response = await BasicAuthServiceHelper.requestAuthenticatedAsync(path, method, payload)
  return response
}

export const getTendersAsync = async (id?: string, status?: string) => {
  let { path, method } = APIRoutes.GetTenders(id)
  if (status) {
    const queryParams = new URLSearchParams()
    queryParams.append('status', status)
    const queryString = queryParams.toString()
    path += path.includes('?') ? `&${queryString}` : `?${queryString}`
  }
  const response = await BasicAuthServiceHelper.requestAuthenticatedAsync(path, method)
  return response
}

export const getTendersPaymentIntentAsync = async (paymentIntentIRI: IRI) => {
  const { path: urlPath, method } = APIRoutes.GetTendersPaymentIntent(paymentIntentIRI)

  const response = await BasicAuthServiceHelper.requestAuthenticatedAsync<{ clientSecret: string }>(urlPath, method)
  return response
}

export const acceptTendersAsync = async (acceptIRI: IRI) => {
  const { path: urlPath, method } = APIRoutes.AcceptTenders(acceptIRI)

  const response = await BasicAuthServiceHelper.requestAuthenticatedAsync(urlPath, method)
  return response
}

export interface TendersResponse {
  '@topic': string
  '@context': string
  '@id': string
  '@type': string
  '@accept': string
  '@reject': string
  '@setup_intent': string
  uuid: string
  customer: {
    '@context': string
    '@id': string
    '@type': string
    '@favorite': string
    '@contact': string
    '@profile': string
    thumbnail: string
    displayName: string
    isFavorite: boolean
    isCertified: boolean
    isPremium: boolean
    isNew: boolean
    id: string
    latitude: number
    longitude: number
    city: string
    zipCode: string
    distance: number
    reviewsSummary: {
      '@context': string
      '@id': string
      '@type': string
      totalReviews: number
      averageRating: number
      reviews: Array<{
        '@context': string
        '@id': string
        '@type': string
        thumbnailUrl: string
        displayName: string
        authorId: string
        reviewDate: string
        rating: number
        comment: string
      }>
    }
    isPetsittingContext: boolean
    ownerId: string
    animals: string[]
  }
  requestDate: string
  animals: any[]
  status: string
  services: Array<{
    '@context': string
    '@id': string
    '@type': string
    offer: string
    start: string
    end: string
    isAdded: boolean
  }>
  message: string
  userCost: number
  petsitterEarnings: number
  defaultSecurityFees: number
  securityFees: number
  customerPremium: boolean
  cancellationPolicies: string
}
